import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Download, Search } from 'lucide-react';
import {
  Button,
  Card,
  CountryBadge,
  EmptyState,
  OperatorBadge,
  SkeletonRow,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { downloadCsv } from '@/lib/utils/csvExport';
import type { ResponseEnvelope } from '@/types/api';

interface OpportunityRecord {
  id: string;
  clientId: string;
  clientName: string | null;
  mobileNumber: string | null;
  operator: string | null;
  countryCode: string | null;
  description: string | null;
  latencyMs: number | null;
  metadataId: string | null;
  createdAt: string;
}

interface OpportunitiesPage {
  content: OpportunityRecord[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

function toInstantParam(date: string, endOfDay = false): string {
  if (!date) return '';
  return `${date}T${endOfDay ? '23:59:59' : '00:00:00'}Z`;
}

async function fetchOpportunities(params: {
  page: number;
  size: number;
  clientId?: string;
  mobile?: string;
  metadataId?: string;
  operator?: string;
  countryCode?: string;
  from?: string;
  to?: string;
}): Promise<OpportunitiesPage> {
  const res = await api.get<ResponseEnvelope<OpportunitiesPage>>(
    Endpoints.dashboard.adminOpportunities,
    { params },
  );
  return unwrap(res);
}

export function AdminOpportunityPage() {
  const [page, setPage] = useState(0);
  const [clientIdFilter, setClientIdFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [metadataIdFilter, setMetadataIdFilter] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('');
  const [countryCodeFilter, setCountryCodeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const queryParams = {
    page,
    size: 25,
    clientId: clientIdFilter || undefined,
    mobile: mobileFilter || undefined,
    metadataId: metadataIdFilter || undefined,
    operator: operatorFilter || undefined,
    countryCode: countryCodeFilter || undefined,
    from: fromDate ? toInstantParam(fromDate) : undefined,
    to: toDate ? toInstantParam(toDate, true) : undefined,
  };

  const query = useQuery({
    queryKey: [
      'dashboard', 'opportunities', page, clientIdFilter, mobileFilter,
      metadataIdFilter, operatorFilter, countryCodeFilter, fromDate, toDate,
    ],
    queryFn: () => fetchOpportunities(queryParams),
  });

  const rows = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 1;

  function resetFilters() {
    setPage(0);
    setClientIdFilter('');
    setMobileFilter('');
    setMetadataIdFilter('');
    setOperatorFilter('');
    setCountryCodeFilter('');
    setFromDate('');
    setToDate('');
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const exportData = await fetchOpportunities({ ...queryParams, page: 0, size: 5000 });
      const headers = ['ID', 'Client', 'Mobile', 'Metadata ID', 'Operator', 'Country', 'Latency (ms)', 'Cost', 'Date'];
      const csvRows = exportData.content.map((r) => [
        r.id,
        r.clientName ?? r.clientId,
        r.mobileNumber ?? '',
        r.metadataId ?? '',
        r.operator ?? '',
        r.countryCode ?? '',
        r.latencyMs ?? '',
        1,
        r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
      ]);
      downloadCsv(`opportunities-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-fg">Opportunities</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            All opportunity identification records across all clients, newest first.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isExporting || rows.length === 0}
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {isExporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
            <input
              type="search"
              placeholder="Client ID…"
              value={clientIdFilter}
              onChange={(e) => { setClientIdFilter(e.target.value); setPage(0); }}
              className="h-8 w-full rounded-lg border border-border bg-surface pl-8 pr-3 text-xs outline-none focus:border-primary-500"
            />
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
            <input
              type="search"
              placeholder="Mobile number…"
              value={mobileFilter}
              onChange={(e) => { setMobileFilter(e.target.value); setPage(0); }}
              className="h-8 w-full rounded-lg border border-border bg-surface pl-8 pr-3 text-xs outline-none focus:border-primary-500"
            />
          </div>
          <input
            type="search"
            placeholder="Metadata ID…"
            value={metadataIdFilter}
            onChange={(e) => { setMetadataIdFilter(e.target.value); setPage(0); }}
            className="h-8 rounded-lg border border-border bg-surface px-3 text-xs outline-none focus:border-primary-500"
          />
          <input
            type="search"
            placeholder="Operator…"
            value={operatorFilter}
            onChange={(e) => { setOperatorFilter(e.target.value); setPage(0); }}
            className="h-8 rounded-lg border border-border bg-surface px-3 text-xs outline-none focus:border-primary-500"
          />
          <input
            type="search"
            placeholder="Country code (e.g. JO)…"
            value={countryCodeFilter}
            onChange={(e) => { setCountryCodeFilter(e.target.value.toUpperCase()); setPage(0); }}
            className="h-8 rounded-lg border border-border bg-surface px-3 text-xs outline-none focus:border-primary-500"
          />
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
            className="h-8 rounded-lg border border-border bg-surface px-2 text-xs outline-none focus:border-primary-500"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(0); }}
            className="h-8 rounded-lg border border-border bg-surface px-2 text-xs outline-none focus:border-primary-500"
          />
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-fg-muted hover:text-fg underline-offset-2 hover:underline"
            >
              Reset
            </button>
            {query.data && (
              <span className="text-xs text-fg-subtle">
                {query.data.totalElements.toLocaleString()} records
              </span>
            )}
          </div>
        </div>
      </Card>

      <Card noPadding>
        {query.isLoading ? (
          <div>
            {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} cols={8} />)}
          </div>
        ) : query.isError ? (
          <div className="px-5 py-8">
            <div
              role="alert"
              className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" aria-hidden />
              <div className="flex-1">
                <p className="text-rose-700 dark:text-rose-300">
                  {isApiError(query.error)
                    ? query.error.firstErrorMessage
                    : 'Could not load opportunities. Please try again.'}
                </p>
                <button
                  type="button"
                  onClick={() => query.refetch()}
                  className="mt-1.5 text-xs font-medium text-rose-600 underline-offset-2 hover:underline dark:text-rose-400"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState message="No opportunities match the current filter." />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Client</TH>
                  <TH>Mobile</TH>
                  <TH>Metadata ID</TH>
                  <TH>Operator</TH>
                  <TH>Country</TH>
                  <TH className="text-right">Latency</TH>
                  <TH className="text-right">Cost</TH>
                  <TH>Date</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TR key={row.id}>
                    <TD className="text-xs text-fg-muted">
                      {row.clientName ?? (
                        <span className="font-mono text-[10px]">{row.clientId}</span>
                      )}
                    </TD>
                    <TD className="font-mono text-xs">{row.mobileNumber ?? '—'}</TD>
                    <TD className="font-mono text-[11px] text-fg-subtle">{row.metadataId ?? '—'}</TD>
                    <TD><OperatorBadge operator={row.operator} /></TD>
                    <TD><CountryBadge countryCode={row.countryCode} /></TD>
                    <TD className="text-right text-xs text-fg-muted">
                      {row.latencyMs != null ? `${row.latencyMs}ms` : '—'}
                    </TD>
                    <TD className="text-right text-xs font-semibold text-fg">1</TD>
                    <TD className="text-[11px] text-fg-muted">
                      {new Date(row.createdAt).toLocaleString()}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-border px-4 py-3 sm:px-5">
                <span className="text-xs text-fg-muted">
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
